import { z } from 'zod';

/**
 * Phase 2b M1/M2: sickness and other absence (spec docs/specs/phase-2b-absence.md §2–§3, D44–D45).
 * One typed absence fact per spell from a catalogue of types with HR-editable flags; days are
 * counted from the person's working pattern (apps/api/src/absence/days.ts). These are the
 * shapes the API serves and the web renders. Dates are YYYY-MM-DD; days are decimals.
 */

const IsoDate = z.iso.date();
const Named = z.object({ id: z.uuid(), name: z.string() });

export const AbsenceRangeKindSchema = z.enum(['open', 'range', 'part_day']);
export type AbsenceRangeKind = z.infer<typeof AbsenceRangeKindSchema>;

export const AbsenceApprovalSchema = z.enum(['none', 'manager', 'hr']);
export type AbsenceApproval = z.infer<typeof AbsenceApprovalSchema>;

export const AbsenceStatusSchema = z.enum([
  'open',
  'closed',
  'pending_approval',
  'approved',
  'declined',
  'cancelled',
]);
export type AbsenceStatus = z.infer<typeof AbsenceStatusSchema>;

export const AbsenceSourceSchema = z.enum(['aroo', 'import', 'infringement_readback']);
export type AbsenceSource = z.infer<typeof AbsenceSourceSchema>;

export const AbsenceCertifiedSchema = z.enum(['self', 'fit_note', 'none']);
export type AbsenceCertified = z.infer<typeof AbsenceCertifiedSchema>;

/** AM/PM on the first or last day of a spell (same values as the holiday half). */
export const AbsencePartSchema = z.enum(['am', 'pm']);
export type AbsencePart = z.infer<typeof AbsencePartSchema>;

export const ABSENCE_STATUS_LABELS: Record<AbsenceStatus, string> = {
  open: 'Open',
  closed: 'Closed',
  pending_approval: 'Awaiting approval',
  approved: 'Approved',
  declined: 'Declined',
  cancelled: 'Cancelled',
};

export const ABSENCE_CERTIFIED_LABELS: Record<AbsenceCertified, string> = {
  self: 'Self-certified',
  fit_note: 'Fit note',
  none: 'Not certified',
};

/** Statuses that count as "on the books": one person may not hold two live absences on a day. */
export const ABSENCE_LIVE_STATUSES = ['open', 'pending_approval', 'approved'] as const;

/** The three groups the today list and the calendar legend use. */
export const AbsenceGroupSchema = z.enum(['sickness', 'awol', 'other']);
export type AbsenceGroup = z.infer<typeof AbsenceGroupSchema>;

export function absenceGroupOf(typeCode: string): AbsenceGroup {
  if (typeCode === 'sickness') return 'sickness';
  if (typeCode === 'awol') return 'awol';
  return 'other';
}

// Catalogue ----------------------------------------------------------------------------

export const AbsenceTypeSchema = z.object({
  code: z.string(),
  name: z.string(),
  rangeKind: AbsenceRangeKindSchema,
  allowsPartDay: z.boolean(),
  needsApproval: AbsenceApprovalSchema,
  /** Colleagues see the reason on calendars; false shows "Absent". */
  visibleToStaff: z.boolean(),
  active: z.boolean(),
  sort: z.number().int(),
  // The HR-only flags (absence.admin.manage); absent for everyone else.
  countsToBradford: z.boolean().optional(),
  requiresRtw: z.boolean().optional(),
  fitNoteAfterDays: z.number().int().nullable().optional(),
  payCategory: z.string().optional(),
});
export type AbsenceType = z.infer<typeof AbsenceTypeSchema>;

export const AbsenceTypesResponseSchema = z.object({
  types: z.array(AbsenceTypeSchema),
  /** True when the HR-only flags are included. */
  full: z.boolean(),
});
export type AbsenceTypesResponse = z.infer<typeof AbsenceTypesResponseSchema>;

// Absences -----------------------------------------------------------------------------

export const AbsenceEventSchema = z.object({
  from: AbsenceStatusSchema.nullable(),
  to: AbsenceStatusSchema,
  by: Named.nullable(),
  at: z.iso.datetime(),
  note: z.string().nullable(),
});
export type AbsenceEvent = z.infer<typeof AbsenceEventSchema>;

export const ReturnToWorkSchema = z.object({
  heldOn: IsoDate,
  heldBy: Named.nullable(),
  reasonDiscussed: z.string().nullable(),
  fitForWork: z.boolean(),
  adjustmentsText: z.string().nullable(),
  fitNoteSeen: z.boolean(),
  followUpOn: IsoDate.nullable(),
});
export type ReturnToWork = z.infer<typeof ReturnToWorkSchema>;

/** What the person sees of their own absence: dates, type, days; no manager notes. */
export const MyAbsenceSchema = z.object({
  id: z.uuid(),
  type: z.object({ code: z.string(), name: z.string() }),
  startDate: IsoDate,
  /** Null while the spell is open. */
  endDate: IsoDate.nullable(),
  startPart: AbsencePartSchema.nullable(),
  endPart: AbsencePartSchema.nullable(),
  hours: z.number().nullable(),
  /** Working days so far for an open spell, else the stored count. */
  days: z.number(),
  status: AbsenceStatusSchema,
  open: z.boolean(),
  certified: AbsenceCertifiedSchema,
  fitNoteReceivedOn: IsoDate.nullable(),
  expectedReturnOn: IsoDate.nullable(),
  imported: z.boolean(),
  /** The decline reason (or approval note) the person may see; null otherwise. */
  decisionNote: z.string().nullable(),
});
export type MyAbsence = z.infer<typeof MyAbsenceSchema>;

/** The manager/HR view: the person's own fields plus notes, who recorded it and the history. */
export const AbsenceSchema = MyAbsenceSchema.extend({
  personId: z.uuid(),
  reasonNote: z.string().nullable(),
  recordedBy: Named.nullable(),
  recordedAt: z.iso.datetime(),
  source: AbsenceSourceSchema,
  /** The spell overlaps an approved holiday (M2 returns the days; M1 only flags it). */
  overlapsHoliday: z.boolean(),
  decidedBy: Named.nullable(),
  decidedAt: z.iso.datetime().nullable(),
  cancelledAt: z.iso.datetime().nullable(),
  cancelReason: z.string().nullable(),
  /** Null until the RTW conversation is recorded. */
  returnToWork: ReturnToWorkSchema.nullable(),
  /** Closed, the type requires an RTW and none is held yet. */
  rtwDue: z.boolean(),
  events: z.array(AbsenceEventSchema),
});
export type Absence = z.infer<typeof AbsenceSchema>;

/** GET /absence/me */
export const MyAbsenceResponseSchema = z.object({
  person: z.object({ id: z.uuid(), name: z.string(), firstName: z.string() }),
  today: IsoDate,
  /** Newest first. */
  absences: z.array(MyAbsenceSchema),
});
export type MyAbsenceResponse = z.infer<typeof MyAbsenceResponseSchema>;

/** GET /absence/people/:id */
export const AbsenceTimelineSchema = z.object({
  person: z.object({ id: z.uuid(), name: z.string(), firstName: z.string() }),
  today: IsoDate,
  /** Newest first. */
  absences: z.array(AbsenceSchema),
  /** Ids of the spells still open (sickness, AWOL) as of today. */
  openIds: z.array(z.uuid()),
  /** Rolling 365 days to today, sickness only: the inputs Bradford will use (M3). */
  lastYear: z.object({ spells: z.number().int(), days: z.number() }),
});
export type AbsenceTimeline = z.infer<typeof AbsenceTimelineSchema>;

// Today ----------------------------------------------------------------------------------

/** GET /absence/today?site=|region=&date= : a site or region in scope (default: the whole reach). */
export const AbsenceTodayQuerySchema = z.object({
  site: z.string().trim().max(32).optional(),
  region: z.string().trim().max(32).optional(),
  /** Defaults to today (Europe/London). */
  date: IsoDate.optional(),
});
export type AbsenceTodayQuery = z.infer<typeof AbsenceTodayQuerySchema>;

export const AbsenceTodayPersonSchema = z.object({
  absenceId: z.uuid(),
  personId: z.uuid(),
  name: z.string(),
  initials: z.string(),
  jobTitle: z.string().nullable(),
  type: z.object({ code: z.string(), name: z.string() }),
  group: AbsenceGroupSchema,
  startDate: IsoDate,
  endDate: IsoDate.nullable(),
  startPart: AbsencePartSchema.nullable(),
  endPart: AbsencePartSchema.nullable(),
  open: z.boolean(),
  /** Working days from the start to the date asked for (inclusive). */
  daysSoFar: z.number(),
  expectedReturnOn: IsoDate.nullable(),
  certified: AbsenceCertifiedSchema,
});
export type AbsenceTodayPerson = z.infer<typeof AbsenceTodayPersonSchema>;

export const AbsenceTodayResponseSchema = z.object({
  date: IsoDate,
  today: IsoDate,
  scope: z.object({
    kind: z.enum(['company', 'region', 'site']),
    code: z.string().nullable(),
    name: z.string(),
  }),
  sites: z.array(
    z.object({
      code: z.string(),
      name: z.string(),
      people: z.array(AbsenceTodayPersonSchema),
    }),
  ),
  totals: z.object({
    sickness: z.number().int(),
    awol: z.number().int(),
    other: z.number().int(),
  }),
});
export type AbsenceTodayResponse = z.infer<typeof AbsenceTodayResponseSchema>;

// Calendar layer ---------------------------------------------------------------------------

/** One absence on the branch calendar; staff see hidden types as "Absent". */
export const CalendarAbsenceEntrySchema = z.object({
  absenceId: z.uuid(),
  personId: z.uuid(),
  name: z.string(),
  startDate: IsoDate,
  /** Null while open: the cell layer runs to the end of the month. */
  endDate: IsoDate.nullable(),
  startPart: AbsencePartSchema.nullable(),
  endPart: AbsencePartSchema.nullable(),
  /** Null when the type is not shown to staff. */
  typeCode: z.string().nullable(),
  typeName: z.string(),
  group: AbsenceGroupSchema,
  open: z.boolean(),
});
export type CalendarAbsenceEntry = z.infer<typeof CalendarAbsenceEntrySchema>;

/** 422 when an absence would overlap another live absence of the same person. */
export const AbsenceOverlapErrorSchema = z.object({
  error: z.literal('absence_overlap'),
  message: z.string(),
  clash: z.object({
    id: z.uuid(),
    typeCode: z.string(),
    startDate: IsoDate,
    endDate: IsoDate.nullable(),
    status: AbsenceStatusSchema,
  }),
});
export type AbsenceOverlapError = z.infer<typeof AbsenceOverlapErrorSchema>;

// Phase 2b M2: record, close, edit, cancel, return-to-work -------------------------------

/**
 * POST /absence/people/:id — a manager (absence.scope.record at the person's home site) or HR
 * records an absence. Open-ended types (sickness, AWOL) may leave `endDate` off → status
 * `open`; range and part-day types need an end (a single day may carry a part or hours) and
 * are `approved` at once (the manager is the approver).
 */
export const AbsenceRecordCreateSchema = z.object({
  typeCode: z.string().trim().min(1).max(32),
  startDate: IsoDate,
  endDate: IsoDate.nullable().optional(),
  startPart: AbsencePartSchema.nullable().optional(),
  endPart: AbsencePartSchema.nullable().optional(),
  /** Part-day types: hours away on a single day. */
  hours: z.number().min(0.25).max(24).nullable().optional(),
  note: z.string().trim().max(500).nullable().optional(),
  expectedReturnOn: IsoDate.nullable().optional(),
  certified: AbsenceCertifiedSchema.optional(),
});
export type AbsenceRecordCreate = z.infer<typeof AbsenceRecordCreateSchema>;

/** A holiday booking touched when an absence covered its days (spec §3 "Days"). */
export const HolidayDaysReturnedSchema = z.object({
  bookingId: z.uuid(),
  startDate: IsoDate,
  endDate: IsoDate,
  /** Days given back to the balance. */
  days: z.number(),
  /** `cancelled` (whole booking), `shortened` (one end), `split` (the middle), `flagged` (imported: HR handles). */
  outcome: z.enum(['cancelled', 'shortened', 'split', 'flagged']),
});
export type HolidayDaysReturned = z.infer<typeof HolidayDaysReturnedSchema>;

/** The answer to every absence write: the absence as the timeline shows it, plus any holiday handling. */
export const AbsenceWriteResponseSchema = z.object({
  absence: AbsenceSchema,
  holiday: z.array(HolidayDaysReturnedSchema),
  /** Closed sickness/AWOL with `requires_rtw` and no RTW yet: the form should open. */
  rtwRequired: z.boolean(),
});
export type AbsenceWriteResponse = z.infer<typeof AbsenceWriteResponseSchema>;

/** GET /absence/people/:id/holiday-overlap?startDate=&endDate= — what recording would return. */
export const HolidayOverlapPreviewSchema = z.object({
  days: z.number(),
  bookings: z.array(
    z.object({
      bookingId: z.uuid(),
      startDate: IsoDate,
      endDate: IsoDate,
      days: z.number(),
      /** From the old system: flagged for HR rather than changed. */
      imported: z.boolean(),
      /** Working days of the booking inside the absence. */
      affectedDays: z.number(),
    }),
  ),
});
export type HolidayOverlapPreview = z.infer<typeof HolidayOverlapPreviewSchema>;

/** POST /absence/:id/close — the last day absent (AWOL needs a note: what it became). */
export const AbsenceCloseSchema = z.object({
  endDate: IsoDate,
  endPart: AbsencePartSchema.nullable().optional(),
  note: z.string().trim().max(500).nullable().optional(),
});
export type AbsenceClose = z.infer<typeof AbsenceCloseSchema>;

/** PATCH /absence/:id — dates, note, certification, fit note, expected return; HR may re-type with a note. */
export const AbsencePatchSchema = z.object({
  startDate: IsoDate.optional(),
  /** Only for a spell that already has an end (closing an open one is POST /close). */
  endDate: IsoDate.optional(),
  startPart: AbsencePartSchema.nullable().optional(),
  endPart: AbsencePartSchema.nullable().optional(),
  hours: z.number().min(0.25).max(24).nullable().optional(),
  note: z.string().trim().max(500).nullable().optional(),
  certified: AbsenceCertifiedSchema.optional(),
  fitNoteReceivedOn: IsoDate.nullable().optional(),
  expectedReturnOn: IsoDate.nullable().optional(),
  /** absence.admin.manage only; needs `retypeNote`. */
  typeCode: z.string().trim().min(1).max(32).optional(),
  retypeNote: z.string().trim().max(500).optional(),
});
export type AbsencePatch = z.infer<typeof AbsencePatchSchema>;

/** POST /absence/:id/cancel */
export const AbsenceCancelSchema = z.object({
  reason: z.string().trim().min(1, 'Give a reason').max(500),
});
export type AbsenceCancel = z.infer<typeof AbsenceCancelSchema>;

/** PUT /absence/:id/rtw — the return-to-work conversation (spec §4 "Return"). */
export const ReturnToWorkPutSchema = z.object({
  heldOn: IsoDate,
  reasonDiscussed: z.string().trim().max(1000).nullable().optional(),
  fitForWork: z.boolean(),
  adjustmentsText: z.string().trim().max(1000).nullable().optional(),
  fitNoteSeen: z.boolean(),
  followUpOn: IsoDate.nullable().optional(),
});
export type ReturnToWorkPut = z.infer<typeof ReturnToWorkPutSchema>;

/** The RTW grace: working days after the last day absent before "overdue" (spec §4). */
export const RTW_DUE_WORKING_DAYS = 3;
/** HR joins the RTW task after this many working days. */
export const RTW_HR_WORKING_DAYS = 7;
/** An open AWOL older than this many working days is a Home task. */
export const AWOL_ATTENTION_WORKING_DAYS = 2;

// Phase 2b M2: requests and approvals for approvable types ------------------------------

/** POST /absence/requests (absence.own.request): a type with needsApproval manager|hr. */
export const AbsenceRequestCreateSchema = z.object({
  typeCode: z.string().trim().min(1).max(32),
  startDate: IsoDate,
  endDate: IsoDate,
  startPart: AbsencePartSchema.nullable().optional(),
  endPart: AbsencePartSchema.nullable().optional(),
  hours: z.number().min(0.25).max(24).nullable().optional(),
  note: z.string().trim().max(500).nullable().optional(),
});
export type AbsenceRequestCreate = z.infer<typeof AbsenceRequestCreateSchema>;

/** The person's own request after a write: their view plus who it waits for. */
export const AbsenceRequestResponseSchema = z.object({
  absence: MyAbsenceSchema,
  primary: z.object({ id: z.uuid(), name: z.string() }).nullable(),
});
export type AbsenceRequestResponse = z.infer<typeof AbsenceRequestResponseSchema>;

/** POST /absence/requests/:id/withdraw */
export const AbsenceWithdrawResponseSchema = z.object({ absence: MyAbsenceSchema });
export type AbsenceWithdrawResponse = z.infer<typeof AbsenceWithdrawResponseSchema>;

const ApprovalPersonRef = z.object({
  id: z.uuid(),
  name: z.string(),
  firstName: z.string(),
  initials: z.string(),
  jobTitle: z.string().nullable(),
  site: z.object({ code: z.string(), name: z.string() }).nullable(),
});

/** One pending absence request in the unified approvals list. */
export const AbsenceApprovalItemSchema = z.object({
  id: z.uuid(),
  person: ApprovalPersonRef,
  type: z.object({ code: z.string(), name: z.string() }),
  /** Who decides: the line (manager) or HR + admins (hr). */
  approval: z.enum(['manager', 'hr']),
  startDate: IsoDate,
  endDate: IsoDate,
  startPart: AbsencePartSchema.nullable(),
  endPart: AbsencePartSchema.nullable(),
  hours: z.number().nullable(),
  days: z.number(),
  note: z.string().nullable(),
  requestedAt: z.iso.datetime(),
  waitingDays: z.number().int().min(0),
  /** Someone else at the site is off (holiday or absence) on one of the days. */
  clash: z.boolean(),
  primary: z.object({ id: z.uuid(), name: z.string() }).nullable(),
});
export type AbsenceApprovalItem = z.infer<typeof AbsenceApprovalItemSchema>;

export const AbsenceApprovalsResponseSchema = z.object({
  items: z.array(AbsenceApprovalItemSchema),
});
export type AbsenceApprovalsResponse = z.infer<typeof AbsenceApprovalsResponseSchema>;

const CoverCell = z.enum(['R', 'H', 'A', 'P', 'B', '.']);

/** GET /absence/approvals/:id: the item, the who's-off grid (as holidays), clashes, history. */
export const AbsenceApprovalDetailSchema = AbsenceApprovalItemSchema.extend({
  whosOff: z.object({
    columns: z.array(
      z.object({ date: IsoDate, label: z.string(), bankHoliday: z.string().nullable() }),
    ),
    rows: z.array(
      z.object({
        personId: z.uuid(),
        name: z.string(),
        jobTitle: z.string().nullable(),
        cells: z.array(CoverCell),
      }),
    ),
    more: z.number().int().min(0),
  }),
  clashes: z.array(
    z.object({
      personId: z.uuid(),
      name: z.string(),
      startDate: IsoDate,
      endDate: IsoDate,
      /** "holiday" / "sickness" / the absence type name. */
      what: z.string(),
    }),
  ),
  events: z.array(AbsenceEventSchema),
});
export type AbsenceApprovalDetail = z.infer<typeof AbsenceApprovalDetailSchema>;

export const AbsenceDecisionResponseSchema = z.object({ absence: AbsenceSchema });
export type AbsenceDecisionResponse = z.infer<typeof AbsenceDecisionResponseSchema>;

// Phase 2b M2: attention lists (RTW overdue, fit notes due) --------------------------------

/** One row of HR › Absence › RTW overdue / Fit notes due. */
export const AbsenceAttentionItemSchema = z.object({
  absenceId: z.uuid(),
  personId: z.uuid(),
  name: z.string(),
  initials: z.string(),
  jobTitle: z.string().nullable(),
  site: z.object({ code: z.string(), name: z.string() }).nullable(),
  type: z.object({ code: z.string(), name: z.string() }),
  startDate: IsoDate,
  endDate: IsoDate.nullable(),
  days: z.number(),
  /** RTW: the last day absent. Fit note: the day the spell passed the threshold. */
  since: IsoDate,
  /** Working days past the point it became due. */
  overdueWorkingDays: z.number().int().min(0),
});
export type AbsenceAttentionItem = z.infer<typeof AbsenceAttentionItemSchema>;

export const AbsenceAttentionResponseSchema = z.object({
  today: IsoDate,
  items: z.array(AbsenceAttentionItemSchema),
});
export type AbsenceAttentionResponse = z.infer<typeof AbsenceAttentionResponseSchema>;
